import React, { memo, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Watermark } from 'antd';
import type { SubtitleOverlayPlayerRef } from '../../subtitleOverlayPlayer/SubtitleOverlayPlayer';
import { VideoEditSubtitleOverlayPlayer } from './VideoEditSubtitleOverlayPlayer';
import type { VideoOrientation } from './useVideoOrientation';

/** 以此宽度为「设计稿」基准，预览区变宽变窄时字号/间距同比缩放，相对画面比例保持稳定 */
const WATERMARK_REFERENCE_WIDTH = 360;

function clamp(n: number, min: number, max: number) {
    return Math.min(max, Math.max(min, n));
}

/**
 * 根据预览容器宽度计算 antd Watermark 的 font / gap / offset（浅、小、随容器等比）
 */
function useVideoPreviewWatermarkMetrics() {
    const scopeRef = useRef<HTMLDivElement>(null);
    const [scopeWidth, setScopeWidth] = useState(WATERMARK_REFERENCE_WIDTH);

    useLayoutEffect(() => {
        const el = scopeRef.current;
        if (!el || typeof ResizeObserver === 'undefined') {
            return;
        }

        const measure = () => {
            const w = el.getBoundingClientRect().width;
            setScopeWidth(w > 0 ? w : WATERMARK_REFERENCE_WIDTH);
        };

        measure();
        const ro = new ResizeObserver(() => {
            measure();
        });
        ro.observe(el);
        return () => {
            ro.disconnect();
        };
    }, []);

    return useMemo(() => {
        const scale = clamp(scopeWidth / WATERMARK_REFERENCE_WIDTH, 0.45, 3.2);
        const fontSize = Math.max(9, Math.round(13 * scale));
        const gapX = Math.max(34, Math.round(72 * scale));
        const gapY = Math.max(34, Math.round(72 * scale));
        const ox = Math.max(12, Math.round(26 * scale));
        const oy = Math.max(12, Math.round(26 * scale));
        return {
            scopeRef,
            font: {
                color: 'rgba(255, 255, 255, 0.18)',
                fontSize,
            },
            gap: [gapX, gapY] as [number, number],
            offset: [ox, oy] as [number, number],
        };
    }, [scopeWidth]);
}

/** antd Watermark 根节点需占满预览区，内部再用 flex 做上下居中（否则子节点高度随内容贴顶） */
const watermarkRootStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
};

const watermarkInnerFlex = (align: 'left' | 'center'): React.CSSProperties => ({
    flex: 1,
    width: '100%',
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: align === 'left' ? 'flex-end' : 'center',
});

type VideoPreviewLeftProps = {
    /** 横屏上下叠放时与右侧一致水平居中；竖屏左右分栏时保持靠内对齐 */
    orientation?: VideoOrientation;
    isAdmin: boolean;
    isUser?: boolean;
    displayName: string;
    sourceVideoRef: React.RefObject<HTMLVideoElement | null>;
    videoSrc?: string;
    isLoadingVideo: boolean;
    hasVideoData: boolean;
    onRefetchVideoData: () => void;
    onTimeUpdate: any;
    onLoadedMetadata: any;
    onPlay: () => void;
    onPause: () => void; // non-admin pause behavior
    onPauseAdmin?: () => void; // admin pause behavior
    onEnded?: () => void;
    onError: (e: any) => void;
};

export const VideoPreviewLeft = memo((props: VideoPreviewLeftProps) => {
    const {
        orientation = 'portrait',
        isAdmin,
        isUser = false,
        displayName,
        sourceVideoRef,
        videoSrc,
        isLoadingVideo,
        hasVideoData,
        onRefetchVideoData,
        onTimeUpdate,
        onLoadedMetadata,
        onPlay,
        onPause,
        onEnded,
        onError,
    } = props;

    const effectivePause = isAdmin ? (props.onPauseAdmin ?? onPause) : onPause;
    const wm = useVideoPreviewWatermarkMetrics();

    const videoEl = (
        <video
            ref={sourceVideoRef}
            src={videoSrc}
            onTimeUpdate={onTimeUpdate}
            onLoadedMetadata={onLoadedMetadata}
            onPlay={onPlay}
            onPause={effectivePause}
            onEnded={onEnded}
            onError={onError}
            onLoadStart={() => {
                // keep original behavior: no-op
            }}
            onCanPlay={() => {
                // keep original behavior: no-op
            }}
            style={{ width: '96%', objectFit: 'contain' }}
        />
    );

    return (
        <div className="video-show-left video-show-item">
            {/* 管理员与普通用户不显示水印；其余角色显示 */}
            {!isAdmin && !isUser ? (
                <div
                    ref={wm.scopeRef}
                    className="video-preview-watermark-scope"
                    style={{
                        width: '100%',
                        height: '100%',
                        minHeight: 0,
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Watermark
                        content={displayName}
                        className="watermark"
                        style={watermarkRootStyle}
                        font={wm.font}
                        gap={wm.gap}
                        offset={wm.offset}
                    >
                        <div
                            className="video-preview-watermark-inner video-preview-watermark-inner--left"
                            style={watermarkInnerFlex(orientation === 'landscape' ? 'center' : 'left')}
                        >
                            {videoEl}
                        </div>
                    </Watermark>
                </div>
            ) : (
                <div
                    style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: orientation === 'landscape' ? 'center' : 'flex-end',
                    }}
                >
                    {videoEl}
                </div>
            )}

            {/* 加载状态覆盖层 */}
            {isLoadingVideo && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        color: 'white',
                        zIndex: 10,
                    }}
                >
                    加载视频中...
                </div>
            )}

            {/* 无数据状态覆盖层 */}
            {!isLoadingVideo && !hasVideoData && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        color: 'white',
                        zIndex: 10,
                    }}
                >
                    <p style={{ marginBottom: '16px' }}>暂无视频数据</p>
                    <button
                        type="button"
                        onClick={onRefetchVideoData}
                        style={{
                            padding: '6px 14px',
                            fontSize: '13px',
                            fontWeight: 500,
                            backgroundColor: 'rgba(255, 255, 255, 0.08)',
                            color: 'rgba(255, 255, 255, 0.88)',
                            border: '1px solid rgba(255, 255, 255, 0.28)',
                            borderRadius: '6px',
                            cursor: 'pointer',
                        }}
                    >
                        重新获取视频
                    </button>
                </div>
            )}
        </div>
    );
});

type VideoPreviewRightProps = {
    isAdmin: boolean;
    isUser?: boolean;
    displayName: string;
    subtitleOverlayPlayerRef: React.RefObject<SubtitleOverlayPlayerRef | null>;
    isLoadingVideo: boolean;
    videoDataForDemo: any;
    localSubtitlesForDemo: any;
    targetTextForDemo: any;
    id?: string;
    overlayTopPx: number;
    /** fix-code-place：多语种预览可拖拽/缩放字幕 */
    interactive?: boolean;
    /** fix-code：有值时右侧叠加字幕按台词行 id 显示（与 subtitles[].id 一致） */
    overlayDisplayLockLineId?: number | null;
    onExternalPlay: () => void;
    onExternalPause: () => void;
    onExternalSeek: (time: number) => void;
    onRightVideoReady: () => void;
    onSubtitlePostTextChange?: (payload: { id: number; postText: string }) => void;
};

export const VideoPreviewRight = memo((props: VideoPreviewRightProps) => {
    const {
        isAdmin,
        isUser = false,
        displayName,
        subtitleOverlayPlayerRef,
        isLoadingVideo,
        videoDataForDemo,
        localSubtitlesForDemo,
        targetTextForDemo,
        id,
        overlayTopPx,
        interactive,
        overlayDisplayLockLineId,
        onExternalPlay,
        onExternalPause,
        onExternalSeek,
        onRightVideoReady,
        onSubtitlePostTextChange,
    } = props;

    const wm = useVideoPreviewWatermarkMetrics();

    const demo = (
        <VideoEditSubtitleOverlayPlayer
            key={`subtitle-overlay-player-${id || 'default'}`}
            ref={subtitleOverlayPlayerRef}
            taskId={id}
            videoData={videoDataForDemo}
            localSubtitles={localSubtitlesForDemo}
            targetText={targetTextForDemo}
            overlayTopPx={overlayTopPx}
            interactive={interactive}
            overlayDisplayLockLineId={overlayDisplayLockLineId}
            onExternalPlay={onExternalPlay}
            onExternalPause={onExternalPause}
            onExternalSeek={onExternalSeek}
            onRightVideoReady={onRightVideoReady}
            onSubtitlePostTextChange={onSubtitlePostTextChange}
        />
    );

    return (
        <div className="video-show-right video-show-item">
            {/* 管理员与普通用户不显示水印；其余角色显示 */}
            {!isAdmin && !isUser ? (
                <div
                    ref={wm.scopeRef}
                    className="video-preview-watermark-scope"
                    style={{
                        width: '100%',
                        height: '100%',
                        minHeight: 0,
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Watermark
                        content={displayName}
                        className="watermark"
                        style={watermarkRootStyle}
                        font={wm.font}
                        gap={wm.gap}
                        offset={wm.offset}
                    >
                        <div className="video-preview-watermark-inner" style={watermarkInnerFlex('center')}>
                            {isLoadingVideo ? (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%',
                                        color: '#666',
                                    }}
                                >
                                    加载视频中...
                                </div>
                            ) : (
                                demo
                            )}
                        </div>
                    </Watermark>
                </div>
            ) : (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {isLoadingVideo ? (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                color: '#666',
                            }}
                        >
                            加载视频中...
                        </div>
                    ) : (
                        demo
                    )}
                </div>
            )}
        </div>
    );
});

